export const coordinateFormats = ['decimal', 'wgs', 'wgs-dms'] as const;

export type CoordinateFormat = (typeof coordinateFormats)[number];
export type CoordinateAxis = 'latitude' | 'longitude';

export type CoordinatePairValue = {
  latitude: number;
  longitude: number;
};

const PRIME_CHARS = /[\u2018\u2019\u2032]/g;
const DOUBLE_PRIME_CHARS = /[\u201C\u201D\u2033]/g;

const normalizeCoordinateInput = (value: string): string => {
  return value
    .trim()
    .replace(PRIME_CHARS, "'")
    .replace(DOUBLE_PRIME_CHARS, '"')
    .replace(/[º]/g, '°')
    .replace(/,/g, '.')
    .replace(/\s+/g, ' ')
    .toUpperCase();
};

const getAxisMax = (axis: CoordinateAxis) => (axis === 'latitude' ? 90 : 180);
const getDegreeWidth = (axis: CoordinateAxis) => (axis === 'latitude' ? 2 : 3);

const isWithinRange = (value: number, axis: CoordinateAxis) => {
  return Number.isFinite(value) && Math.abs(value) <= getAxisMax(axis);
};

const padDegrees = (value: number, axis: CoordinateAxis) => {
  return String(value).padStart(getDegreeWidth(axis), '0');
};

const roundTo = (value: number, digits: number) => {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};

const formatSignedDecimal = (value: number) => {
  const normalized = roundTo(value, 6);
  return normalized.toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
};

const parseDecimalCoordinate = (value: string, axis: CoordinateAxis): number | null => {
  if (!/^[+-]?\d+(?:\.\d+)?$/.test(value)) return null;
  const numeric = Number(value);
  return isWithinRange(numeric, axis) ? numeric : null;
};

const parseStructuredCoordinate = (value: string, axis: CoordinateAxis): number | null => {
  const normalized = normalizeCoordinateInput(value);
  const hemisphereMatch = normalized.match(/[NSEW]/g) ?? [];
  const hemisphere = hemisphereMatch[0] ?? null;

  const signFromHemisphere =
    hemisphere === 'S' || hemisphere === 'W'
      ? -1
      : hemisphere === 'N' || hemisphere === 'E'
        ? 1
        : null;

  const sign = normalized.startsWith('-') ? -1 : (signFromHemisphere ?? 1);
  const stripped = normalized
    .replace(/[NSEW]/g, '')
    .replace(/^[+-]/, '')
    .trim();

  const dmsMatch = stripped.match(
    /^(\d{1,3})\s*°\s*(\d{1,2})\s*['′]?\s*(\d{1,2}(?:\.\d+)?)\s*(?:"|''|”)?$/
  );
  if (dmsMatch) {
    const degrees = Number(dmsMatch[1]);
    const minutes = Number(dmsMatch[2]);
    const seconds = Number(dmsMatch[3]);
    if (minutes >= 60 || seconds >= 60) return null;
    const decimal = sign * (degrees + minutes / 60 + seconds / 3600);
    return isWithinRange(decimal, axis) ? decimal : null;
  }

  const wgsMatch = stripped.match(/^(\d{1,3})\s*°\s*(\d{1,2}(?:\.\d+)?)\s*['′]?$/);
  if (wgsMatch) {
    const degrees = Number(wgsMatch[1]);
    const minutes = Number(wgsMatch[2]);
    if (minutes >= 60) return null;
    const decimal = sign * (degrees + minutes / 60);
    return isWithinRange(decimal, axis) ? decimal : null;
  }

  return null;
};

export const parseCoordinateValue = (value: unknown, axis: CoordinateAxis): number | null => {
  if (typeof value === 'number') {
    return isWithinRange(value, axis) ? value : null;
  }

  if (typeof value !== 'string') return null;

  const normalized = normalizeCoordinateInput(value);
  if (!normalized) return null;

  return parseDecimalCoordinate(normalized, axis) ?? parseStructuredCoordinate(normalized, axis);
};

export const formatCoordinateValue = (
  value: number | string,
  axis: CoordinateAxis,
  format: CoordinateFormat
): string => {
  const parsed = parseCoordinateValue(value, axis);
  if (parsed === null) {
    return typeof value === 'string' ? value : '';
  }

  if (format === 'decimal') {
    return formatSignedDecimal(parsed);
  }

  const absolute = Math.abs(parsed);
  const degrees = Math.floor(absolute);
  const remainderMinutes = (absolute - degrees) * 60;

  if (format === 'wgs') {
    const minutes = roundTo(remainderMinutes, 3);
    if (minutes >= 60) {
      return `${parsed < 0 ? '-' : ''}${padDegrees(degrees + 1, axis)}°00.000'`;
    }
    return `${parsed < 0 ? '-' : ''}${padDegrees(degrees, axis)}°${minutes.toFixed(3).padStart(6, '0')}'`;
  }

  const minutesWhole = Math.floor(remainderMinutes);
  const seconds = roundTo((remainderMinutes - minutesWhole) * 60, 2);
  let nextDegrees = degrees;
  let nextMinutes = minutesWhole;
  let nextSeconds = seconds;

  if (nextSeconds >= 60) {
    nextSeconds = 0;
    nextMinutes += 1;
  }
  if (nextMinutes >= 60) {
    nextMinutes = 0;
    nextDegrees += 1;
  }

  return `${parsed < 0 ? '-' : ''}${padDegrees(nextDegrees, axis)}°${String(nextMinutes).padStart(2, '0')}'${nextSeconds.toFixed(2).padStart(5, '0')}"`;
};

export const formatCoordinatePair = (
  value: CoordinatePairValue,
  format: CoordinateFormat
): string => {
  return `${formatCoordinateValue(value.latitude, 'latitude', format)}, ${formatCoordinateValue(value.longitude, 'longitude', format)}`;
};

export const defaultCoordinateFormat: CoordinateFormat = 'wgs-dms';
